import React from 'react';
import { BrowserRouter, Routes as RouterRoutes, Route } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import Footer from './components/ui/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import ToastProvider from './components/ToastNotification';
import useApiErrorHandler from './hooks/useApiErrorHandler';
import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';

// Import pages
import Dashboard from './pages/dashboard';
import BibleReader from './pages/bible-reader';
import VerseLibrary from './pages/verse-library';
import MemorizationPractice from './pages/memorization-practice';
import BibleReadingPlans from './pages/bible-reading-plans';
import DailyReviewSystem from './pages/daily-review-system';
import StudyAnalyticsDashboard from './pages/study-analytics-dashboard';
import Login from './pages/login';
import SignUp from './pages/signup';
import ForgotPassword from './pages/forgot-password';
import ResetPassword from './pages/reset-password';
import UserProfile from './pages/user-profile';
import AdminDashboard from './pages/admin-dashboard';
import BibleVersionImport from './pages/bible-version-import';
import BibleVersionManagement from './pages/bible-version-management';
import UserManagement from './pages/user-management';
import ReadingPlanManagement from './pages/reading-plan-management';
import VerseListManagement from './pages/verse-list-management';
import PrivacyPolicy from './pages/privacy-policy';
import TermsOfService from './pages/terms-of-service';
import Tutorial from './pages/tutorial';
import NotFound from './pages/NotFound';
import EGWPlanManagement from './pages/egw-plan-management';
import ContactUs from './pages/contact-us';
import About from './pages/about';
import AdminAuditLog from './pages/admin-audit-log';
import SecurityManagement from './pages/security-management';

// Study Notes
import StudyNotesList from './pages/study-notes/index';
import StudyNoteEditor from './pages/study-notes/editor';
import PresenterView from './pages/study-notes/presenter';
import AudienceView from './pages/study-notes/audience';

// ── Per-page error boundary fallback components ──────────────────────────────

class DailyReviewBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(e, i) { console.error('[DailyReview] crash:', e, i); }
  render() {
    if (this.state?.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
          <div className="text-center p-8 max-w-md w-full">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
              </div>
            </div>
            <h1 className="text-2xl font-semibold text-neutral-800 mb-2">Daily Review Unavailable</h1>
            <p className="text-neutral-600 text-sm mb-6">Something went wrong while loading your Daily Review session. Your progress has been saved — please try again or return to the dashboard.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => this.setState({ hasError: false })} className="inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded transition-colors shadow-sm">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                Try Again
              </button>
              <button onClick={() => { window.location.href = '/dashboard'; }} className="inline-flex items-center justify-center gap-2 border border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-700 font-medium py-2 px-4 rounded transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props?.children;
  }
}

class MemorizationPracticeBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(e, i) { console.error('[MemorizationPractice] crash:', e, i); }
  render() {
    if (this.state?.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
          <div className="text-center p-8 max-w-md w-full">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96-.46 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"/><path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96-.46 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"/></svg>
              </div>
            </div>
            <h1 className="text-2xl font-semibold text-neutral-800 mb-2">Practice Session Interrupted</h1>
            <p className="text-neutral-600 text-sm mb-6">An unexpected error occurred during your Memorization Practice session. Don't worry — your verse library is safe. Try restarting the session.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => this.setState({ hasError: false })} className="inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded transition-colors shadow-sm">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                Restart Session
              </button>
              <button onClick={() => window.history?.back()} className="inline-flex items-center justify-center gap-2 border border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-700 font-medium py-2 px-4 rounded transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                Go Back
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props?.children;
  }
}

class VerseLibraryBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(e, i) { console.error('[VerseLibrary] crash:', e, i); }
  render() {
    if (this.state?.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
          <div className="text-center p-8 max-w-md w-full">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m16 6 4 14"/><path d="M12 6v14"/><path d="M8 8v12"/><path d="M4 4v16"/></svg>
              </div>
            </div>
            <h1 className="text-2xl font-semibold text-neutral-800 mb-2">Verse Library Unavailable</h1>
            <p className="text-neutral-600 text-sm mb-6">We ran into a problem loading your Verse Library. Your verses are stored safely — please refresh to try again or return to the dashboard.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => this.setState({ hasError: false })} className="inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded transition-colors shadow-sm">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                Reload Library
              </button>
              <button onClick={() => { window.location.href = '/dashboard'; }} className="inline-flex items-center justify-center gap-2 border border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-700 font-medium py-2 px-4 rounded transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props?.children;
  }
}

// ── End per-page error boundaries ────────────────────────────────────────────

// Inner component so hook can access ToastProvider context
const AppWithErrorHandling = ({ children }) => {
  useApiErrorHandler();
  useGoogleAnalytics();
  return children;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ToastProvider>
          <AppWithErrorHandling>
            <ScrollToTop />
            <div className="flex flex-col min-h-screen">
              <RouterRoutes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/contact-us" element={<ContactUs />} />
                <Route path="/about" element={<About />} />

                {/* Audience View — fully public */}
                <Route path="/live/:slug" element={<AudienceView />} />

                {/* Bible Reader — accessible to guests but shows sign-in banner */}
                <Route path="/bible-reader" element={<BibleReader />} />

                {/* Protected routes — require authentication */}
                <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/verse-library" element={<ProtectedRoute><VerseLibraryBoundary><VerseLibrary /></VerseLibraryBoundary></ProtectedRoute>} />
                <Route path="/memorization-practice" element={<ProtectedRoute><MemorizationPracticeBoundary><MemorizationPractice /></MemorizationPracticeBoundary></ProtectedRoute>} />
                <Route path="/bible-reading-plans" element={<ProtectedRoute><BibleReadingPlans /></ProtectedRoute>} />
                <Route path="/daily-review-system" element={<ProtectedRoute><DailyReviewBoundary><DailyReviewSystem /></DailyReviewBoundary></ProtectedRoute>} />
                <Route path="/study-analytics-dashboard" element={<ProtectedRoute><StudyAnalyticsDashboard /></ProtectedRoute>} />
                <Route path="/user-profile" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
                <Route path="/security" element={<ProtectedRoute><SecurityManagement /></ProtectedRoute>} />
                <Route path="/tutorial" element={<ProtectedRoute><Tutorial /></ProtectedRoute>} />

                {/* Study Notes — Tools Section */}
                <Route path="/tools/notes" element={<ProtectedRoute><StudyNotesList /></ProtectedRoute>} />
                <Route path="/tools/notes/new" element={<ProtectedRoute><StudyNoteEditor /></ProtectedRoute>} />
                <Route path="/tools/notes/:id/edit" element={<ProtectedRoute><StudyNoteEditor /></ProtectedRoute>} />
                <Route path="/tools/notes/present/:slug" element={<ProtectedRoute><PresenterView /></ProtectedRoute>} />

                {/* Admin-only protected routes */}
                <Route path="/admin-dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                <Route path="/bible-version-import" element={<ProtectedRoute><BibleVersionImport /></ProtectedRoute>} />
                <Route path="/bible-version-management" element={<ProtectedRoute><BibleVersionManagement /></ProtectedRoute>} />
                <Route path="/user-management" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
                <Route path="/reading-plan-management" element={<ProtectedRoute><ReadingPlanManagement /></ProtectedRoute>} />
                <Route path="/verse-list-management" element={<ProtectedRoute><VerseListManagement /></ProtectedRoute>} />
                <Route path="/egw-plan-management" element={<ProtectedRoute><EGWPlanManagement /></ProtectedRoute>} />
                <Route path="/admin-audit-log" element={<ProtectedRoute><AdminAuditLog /></ProtectedRoute>} />

                <Route path="*" element={<NotFound />} />
              </RouterRoutes>
              <Footer />
            </div>
          </AppWithErrorHandling>
        </ToastProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;